import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Award, Users, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const TEAM_IMAGE = "https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/co7mv4rs_IslandCoastalElectric-MountainOnly-Logo.V3-VECTOR.png";

const AboutPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { value: '20+', label: 'Years Experience' },
    { value: '2,500+', label: 'Projects Completed' },
    { value: '100%', label: 'Licensed & Insured' },
    { value: '5.0', label: 'Star Rating' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Every project follows strict safety protocols and code compliance to protect your property and family.'
    },
    {
      icon: Award,
      title: 'Quality Craftsmanship',
      description: 'Our BC Hydro Alliance certified electricians deliver work that exceeds industry standards.'
    },
    {
      icon: Users,
      title: 'Customer Focused',
      description: 'We communicate clearly, respect your time, and ensure complete satisfaction with every job.'
    },
    {
      icon: Clock,
      title: 'Reliable Service',
      description: 'When we commit to a timeline, we deliver. Count on us to be there when you need us.'
    }
  ];

  const certifications = [
    'BC Hydro Alliance Member',
    'Red Seal Certified',
    'Fully Bonded & Insured',
    'Commercial & Industrial Licensed'
  ];

  const TESLA_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/vcyr36jn_tesla-certified-installer.jpg";
  const GENERAC_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_b9465b5c-80f5-4fe5-aaaa-c4e865864589/artifacts/40wcnfw6_Generac-Installer.png";

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={LOGO_ICON} alt="" className="h-10 w-auto" />
                <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
                  About Us
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
                Powering Vancouver Island
              </h1>
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Island Coastal Electric is a locally owned electrical contracting company serving Vancouver Island&apos;s residential, commercial, and industrial clients. As a proud member of the BC Hydro Alliance of Energy Professionals, our team brings over two decades of hands-on experience to every project.
              </p>
              <p className="text-white/70 text-lg leading-relaxed">
                From simple repairs to complex commercial installations, we pride ourselves on delivering exceptional quality, clear communication, and reliable service. Our commitment to safety and craftsmanship has made us one of the most trusted electrical contractors on the Island.
              </p>
            </div>
            <div className="relative">
              <img 
                src={TEAM_IMAGE} 
                alt="Island Coastal Electric Team" 
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-electric-teal p-6 rounded-lg">
                <p className="text-primary-dark font-black text-4xl">20+</p>
                <p className="text-primary-dark font-bold uppercase tracking-wider text-sm">Years Experience</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet the Founder Section */}
      <section className="py-24 bg-primary-dark" data-testid="founder-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
              Leadership
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
              Meet the Founder
            </h2>
          </div>
          
          <div className="bg-secondary-dark rounded-2xl p-8 md:p-12 border border-white/5">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-electric-teal/20 rounded-full flex items-center justify-center">
                  <span className="text-electric-teal font-black text-3xl md:text-4xl">LD</span>
                </div>
              </div>
              <div>
                <h3 className="text-white font-bold text-2xl md:text-3xl mb-2">Lee Dunbrack</h3>
                <p className="text-electric-teal font-bold uppercase tracking-wider text-sm mb-6">Founder & Lead Electrician — 20+ Years</p>
                
                <div className="space-y-4 text-white/70 text-lg leading-relaxed">
                  <p>
                    Lee Dunbrack brings over two decades of hands-on electrical expertise to every project Island Coastal Electric takes on. His career began in 2004 on Vancouver&apos;s demanding high-rise construction sites, where precision and safety aren&apos;t optional—they&apos;re everything.
                  </p>
                  <p>
                    After earning his Red Seal certification in 2010 while specializing in commercial and industrial projects, Lee spent years traveling across Canada with a leading industrial firm, tackling complex water and wastewater treatment facilities from coast to coast. These high-stakes environments demanded unwavering attention to code compliance, system reliability, and team coordination.
                  </p>
                  <p>
                    As a former job superintendent and project manager overseeing multi-unit residential, custom homes, and commercial builds in Victoria, Lee developed a reputation for clear communication, on-time delivery, and workmanship that stands behind itself.
                  </p>
                  <p className="text-white font-medium italic border-l-4 border-electric-teal pl-6 py-2">
                    In 2025, he founded Island Coastal Electric with a simple commitment: honest pricing, quality work, and the kind of service that earns referrals—not just reviews.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-electric-teal" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <p className="text-primary-dark font-black text-4xl md:text-5xl">{stat.value}</p>
                <p className="text-primary-dark/70 font-bold uppercase tracking-wider text-sm mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-primary-dark" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
              Our Values
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4">
              What Sets Us Apart
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-secondary-dark p-8 rounded-lg border border-white/5 hover:border-electric-teal/30 transition-colors"
                data-testid={`value-${index}`}
              >
                <div className="w-12 h-12 bg-electric-teal/10 rounded-lg flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-electric-teal" />
                </div>
                <h3 className="text-white font-bold text-xl mb-3">{value.title}</h3>
                <p className="text-white/60">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-secondary-dark" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
                Certifications
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
                Licensed & Certified Professionals
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Our team holds all necessary certifications and licenses required to perform electrical work throughout British Columbia. You can trust that every project is completed to the highest standards of safety and quality.
              </p>
              <Link to="/book-estimate" data-testid="about-cta-btn">
                <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-all btn-glow">
                  Get Free Estimate
                </Button>
              </Link>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {certifications.map((cert) => (
                  <div 
                    key={cert}
                    className="flex items-center gap-3 bg-white/5 p-4 rounded-lg"
                  >
                    <CheckCircle className="w-5 h-5 text-electric-teal flex-shrink-0" />
                    <span className="text-white font-medium">{cert}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg" data-testid="about-tesla-cert">
                  <img src={TESLA_BADGE} alt="Tesla Certified Installer" className="h-14 w-auto rounded bg-white p-0.5" />
                  <span className="text-white font-medium text-sm">Tesla Certified<br/>Installer</span>
                </div>
                <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg" data-testid="about-generac-cert">
                  <img src={GENERAC_BADGE} alt="Generac Certified Installer" className="h-14 w-auto rounded bg-white p-0.5" />
                  <span className="text-white font-medium text-sm">Generac Certified<br/>Installer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-24 bg-primary-dark" data-testid="service-area-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
            Service Area
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
            Serving All of Vancouver Island
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            From Victoria to Campbell River, we provide electrical services throughout Vancouver Island. Our service areas include Victoria, Nanaimo, Duncan, Courtenay, Comox Valley, Parksville, Qualicum Beach, Campbell River, and surrounding communities.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Victoria', 'Nanaimo', 'Duncan', 'Courtenay', 'Comox', 'Parksville', 'Campbell River'].map((city) => (
              <span 
                key={city}
                className="px-4 py-2 bg-white/5 text-white/80 rounded-full text-sm font-medium"
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
